import { AreaChart, Area, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { TrendingUp, DollarSign } from 'lucide-react';
import { RevenueDataPoint } from '../../services/dashboard.service';
import { useCurrency } from '../../hooks/useCurrency';

interface RevenueChartProps {
  data: RevenueDataPoint[];
  loading?: boolean;
  ordersLabel?: string;
}

export function RevenueChart({ data, loading = false, ordersLabel = 'Orders' }: RevenueChartProps) {
  const { currency, formatCurrencySpaced, getCurrencySymbol } = useCurrency();

  // Calculate totals
  const totalRevenue = data.reduce((sum, item) => sum + item.revenue, 0);
  const totalOrders = data.reduce((sum, item) => sum + item.orders, 0);

  const formatCompactAmount = (amount: number) => {
    const symbol = getCurrencySymbol();
    if (Math.abs(amount) >= 1000) {
      return `${symbol}${(amount / 1000).toFixed(0)}k`;
    }
    return `${symbol}${Math.round(amount)}`;
  };

  // Format date for display (e.g., "Jan 15" or "15")
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    
    // If data spans multiple months, show month abbreviation
    if (data.length > 0) {
      const firstDate = new Date(data[0].date);
      const lastDate = new Date(data[data.length - 1].date);
      
      if (firstDate.getMonth() !== lastDate.getMonth()) {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    }
    
    return day.toString();
  };

  // Format chart data
  const chartData = data.map((item) => ({
    date: formatDate(item.date),
    revenue: item.revenue,
    orders: item.orders,
  }));

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
            {payload[0].payload.date}
          </p>
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-indigo-600 rounded-full" />
              <span className="text-xs text-gray-600 dark:text-gray-400">Revenue:</span>
              <span className="text-xs font-medium text-gray-900 dark:text-white">
                {formatCurrencySpaced(payload[0].value)}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-600 rounded-full" />
              <span className="text-xs text-gray-600 dark:text-gray-400">{ordersLabel}:</span>
              <span className="text-xs font-medium text-gray-900 dark:text-white">
                {payload[1]?.value || 0}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-800 transition-all duration-200 hover:shadow-lg hover:-translate-y-1" style={{ height: '380px' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 p-2 rounded-xl">
            <DollarSign className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Revenue</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Daily revenue trend ({currency})</p>
          </div>
        </div>
      </div>

      {loading ? (
        // Loading skeleton
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="h-16 flex-1 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="h-16 flex-1 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
          </div>
          <div className="h-60 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
      ) : data.length === 0 ? (
        // Empty state
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <TrendingUp className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              No revenue data available
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-indigo-50 dark:bg-indigo-900/20 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 bg-indigo-600 rounded-full" />
                <span className="text-xs text-gray-600 dark:text-gray-400">Total Revenue</span>
              </div>
              <div className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                <span className="font-bold text-gray-900 dark:text-indigo-300 text-lg">
                  {formatCurrencySpaced(totalRevenue, false)}
                </span>
              </div>
            </div>

            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-2 h-2 bg-purple-600 rounded-full" />
                <span className="text-xs text-gray-600 dark:text-gray-400">Total {ordersLabel}</span>
              </div>
              <div className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                <span className="font-bold text-gray-900 dark:text-white text-lg">
                  {totalOrders}
                </span>
              </div>
            </div>
          </div>

          {/* Chart */}
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#a855f7" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#a855f7" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#9ca3af', fontSize: 11 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#9ca3af', fontSize: 11 }}
                tickFormatter={(value) => formatCompactAmount(Number(value) || 0)}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="revenue"
                stroke="#6366f1"
                strokeWidth={2.5}
                fillOpacity={1}
                fill="url(#colorRevenue)"
              />
              <Area
                type="monotone"
                dataKey="orders"
                stroke="#a855f7"
                strokeWidth={2.5}
                fillOpacity={1}
                fill="url(#colorOrders)"
                yAxisId={0}
              />
            </AreaChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}
